import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { SettingsImportModal } from '../components/domain/SettingsImportModal.js';
import { RoleManagerModal } from '../components/domain/RoleManagerModal.js';
import { ReferenceHealthCard } from '../components/domain/ReferenceHealthCard.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { CORE_MODULES, MODULE_CATEGORIES, MODULES, type ModuleCategory } from '@cls/shared';

interface CompanySettings {
  name: string;
  shortName: string | null;
  sector: string | null;
  hq: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  adminName: string | null;
  adminPhone: string | null;
  adminEmail: string | null;
}

interface SettingRow { id: string; value: string; order?: number | null; }

interface Agency {
  id: string;
  code: string;
  name: string;
  active: boolean;
  order: number;
}

export interface UsageBadge {
  /** Primary count, rendered in muted text. */
  primary?: number;
  /** Optional alert count, rendered red with ⚠ prefix when > 0. */
  alert?: number;
  /** Custom label for the primary count (defaults to row context). */
  primaryLabel?: string;
}

function UsageBadgeView({ badge }: { badge: UsageBadge }) {
  const primary = badge.primary ?? 0;
  const alert = badge.alert ?? 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 4 }}>
      {primary > 0 ? (
        <span style={{ fontSize: 9, color: 'var(--muted)', padding: '1px 6px', borderRadius: 4, background: 'var(--card)' }}>
          {primary}{badge.primaryLabel ? ` ${badge.primaryLabel}` : ''}
        </span>
      ) : null}
      {alert > 0 ? (
        <span style={{ fontSize: 9, color: 'var(--danger)', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,.12)' }}>
          ⚠{alert}
        </span>
      ) : null}
    </span>
  );
}

function EditableCard({
  title,
  table,
  queryKey,
  placeholder,
  sub,
  listId,
  icon,
  iconColor,
  valueRenderer,
  usageCounts,
  extraActions,
  coreValues,
}: {
  title: string;
  table: string;
  queryKey: string[];
  placeholder: string;
  sub?: string;
  listId: string;
  /** Optional glyph rendered before the row label (matches prototype's per-list icon). */
  icon?: string;
  /** Color for the icon (CSS var or hex). */
  iconColor?: string;
  /** Optional custom rendering for the label (e.g. status badges). Receives the row. */
  valueRenderer?: (row: SettingRow) => ReactNode;
  /** Optional per-row usage badges keyed by row.value. */
  usageCounts?: Record<string, UsageBadge | undefined>;
  /** Optional extra row actions rendered between rename and delete. */
  extraActions?: (row: SettingRow) => ReactNode;
  /** Row values that cannot be deleted (delete button is hidden). */
  coreValues?: readonly string[];
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const { data: items = [] } = useQuery({
    queryKey,
    queryFn: () => api.get<SettingRow[]>(`/settings/${table}`).then((r) => r.data).catch(() => []),
  });
  const add = useMutation({
    mutationFn: async () => api.post(`/settings/${table}`, { value: value.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setValue('');
      toast.success('Added', value.trim());
    },
    onError: () => toast.error('Add failed'),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`/settings/${table}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
    onError: () => toast.error('Delete failed'),
  });
  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api.patch(`/settings/${table}/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
    onError: () => toast.error('Save failed'),
  });
  const reorder = useMutation({
    mutationFn: async (pairs: Array<{ id: string; order: number }>) =>
      Promise.all(pairs.map((p) => api.patch(`/settings/${table}/${p.id}`, { order: p.order }))),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
    onError: () => toast.error('Reorder failed'),
  });

  const commitRename = (id: string) => {
    const v = editingText.trim();
    if (v && v !== items.find((r) => r.id === id)?.value) {
      update.mutate({ id, patch: { value: v } });
    }
    setEditingId(null);
    setEditingText('');
  };

  const commitReorder = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const order = items.map((r) => r.id);
    const fromIx = order.indexOf(fromId);
    const toIx = order.indexOf(toId);
    if (fromIx === -1 || toIx === -1) return;
    const next = [...order];
    next.splice(fromIx, 1);
    next.splice(toIx, 0, fromId);
    reorder.mutate(next.map((id, i) => ({ id, order: i })));
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="sec-title" style={{ margin: 0 }}>{title}</div>
        {sub ? <span style={{ fontSize: 9, color: 'var(--muted)' }}>{sub}</span> : null}
      </div>
      <div id={listId}>
        {items.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 11 }}>None yet.</div>
        ) : items.map((row) => (
          <div
            key={row.id}
            draggable={editingId !== row.id}
            onDragStart={(e) => { setDragId(row.id); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverId !== row.id) setDragOverId(row.id); }}
            onDragLeave={() => { if (dragOverId === row.id) setDragOverId(null); }}
            onDrop={(e) => { e.preventDefault(); if (dragId) commitReorder(dragId, row.id); setDragId(null); setDragOverId(null); }}
            onDragEnd={() => { setDragId(null); setDragOverId(null); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '5px 8px',
              marginBottom: 3,
              borderRadius: 5,
              background: dragOverId === row.id ? 'rgba(232,164,39,.18)' : 'var(--surface)',
              border: dragOverId === row.id ? '1px dashed var(--accent)' : '1px solid transparent',
              opacity: dragId === row.id ? 0.4 : 1,
              cursor: editingId === row.id ? 'text' : 'grab',
            }}
          >
            <span style={{ color: 'var(--faint)', fontSize: 14, marginRight: 4, userSelect: 'none', lineHeight: 1 }} title="Drag to reorder">⠿</span>
            {icon ? (
              <span style={{ width: 18, textAlign: 'center', fontSize: 13, color: iconColor ?? 'var(--muted)', flexShrink: 0 }}>{icon}</span>
            ) : null}
            {editingId === row.id ? (
              <input
                type="text"
                autoFocus
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                onBlur={() => commitRename(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitRename(row.id); }
                  if (e.key === 'Escape') { setEditingId(null); setEditingText(''); }
                }}
                style={{ flex: 1, background: 'var(--card)', border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: 'var(--text)' }}
              />
            ) : valueRenderer ? (
              <span
                style={{ flex: 1, cursor: 'text' }}
                onDoubleClick={() => { setEditingId(row.id); setEditingText(row.value); }}
                title="Double-click to rename"
              >{valueRenderer(row)}</span>
            ) : (
              <span
                style={{ fontSize: 11, flex: 1, cursor: 'text' }}
                onDoubleClick={() => { setEditingId(row.id); setEditingText(row.value); }}
                title="Double-click to rename"
              >{row.value}</span>
            )}
            {usageCounts?.[row.value] ? (
              <UsageBadgeView badge={usageCounts[row.value]!} />
            ) : null}
            {editingId !== row.id && extraActions ? extraActions(row) : null}
            {editingId !== row.id ? (
              <button
                className="btn btn-ghost btn-xs"
                style={{ color: 'var(--muted)', padding: '1px 6px', marginRight: 2 }}
                onClick={() => { setEditingId(row.id); setEditingText(row.value); }}
                title="Rename"
              >✏</button>
            ) : null}
            {coreValues?.includes(row.value) ? (
              <span style={{ fontSize: 9, color: 'var(--faint)', padding: '1px 6px' }} title="Core value — cannot be removed">🔒</span>
            ) : (
              <button
                className="btn btn-ghost btn-xs"
                style={{ color: 'var(--danger)', padding: '1px 6px' }}
                onClick={() => { if (confirm(`Remove "${row.value}"?`)) remove.mutate(row.id); }}
                title="Remove"
              >✕</button>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) add.mutate(); }}
          style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 8px', fontSize: 11, color: 'var(--text)' }}
        />
        <button className="btn btn-primary btn-sm" onClick={() => add.mutate()} disabled={add.isPending || !value.trim()}>+ Add</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import CSV</button>
      </div>
      <div style={{ fontSize: 9, color: 'var(--faint)', marginTop: 5 }}>💡 Drag items to reorder · double-click or ✏ to rename</div>
      {importOpen ? <SettingsImportModal onClose={() => { setImportOpen(false); qc.invalidateQueries({ queryKey }); }} listName={title} endpoint={`/settings/${table}`} /> : null}
    </div>
  );
}

interface CertTypeRow { id: string; value: string; shortKey: string | null; order: number; active: boolean; }

interface WorkerForCounts {
  id: string;
  role: string;
  certs: { type: string; status: string }[];
}

function CertTypesCard() {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const { data: items = [] } = useQuery({
    queryKey: ['settings', 'cert-types'],
    queryFn: () => api.get<CertTypeRow[]>('/settings/cert-types').then((r) => r.data).catch(() => [] as CertTypeRow[]),
  });
  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerForCounts[]>('/workers').then((r) => r.data).catch(() => [] as WorkerForCounts[]),
  });

  // Held + expired counts per cert value, derived from /workers (matches prototype's
  // "X held" + "⚠Y" expired badges per cert type).
  const certCounts = useMemo(() => {
    const counts: Record<string, { held: number; expired: number }> = {};
    for (const w of workers) {
      for (const c of w.certs ?? []) {
        if (!counts[c.type]) counts[c.type] = { held: 0, expired: 0 };
        counts[c.type]!.held += 1;
        if (c.status === 'expired') counts[c.type]!.expired += 1;
      }
    }
    return counts;
  }, [workers]);

  const add = useMutation({
    mutationFn: async () => api.post('/settings/cert-types', { value: value.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'cert-types'] });
      qc.invalidateQueries({ queryKey: ['training', 'matrix'] });
      setValue('');
      toast.success('Added', value.trim());
    },
    onError: () => toast.error('Add failed'),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`/settings/cert-types/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'cert-types'] });
      qc.invalidateQueries({ queryKey: ['training', 'matrix'] });
    },
    onError: () => toast.error('Delete failed'),
  });
  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api.patch(`/settings/cert-types/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'cert-types'] });
      qc.invalidateQueries({ queryKey: ['training', 'matrix'] });
    },
    onError: () => toast.error('Save failed'),
  });
  const reorder = useMutation({
    mutationFn: async (pairs: Array<{ id: string; order: number }>) =>
      Promise.all(pairs.map((p) => api.patch(`/settings/cert-types/${p.id}`, { order: p.order }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'cert-types'] }),
  });

  const commitRename = (id: string) => {
    const v = editingText.trim();
    if (v && v !== items.find((r) => r.id === id)?.value) update.mutate({ id, patch: { value: v } });
    setEditingId(null); setEditingText('');
  };

  const commitReorder = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const order = items.map((r) => r.id);
    const fi = order.indexOf(fromId); const ti = order.indexOf(toId);
    if (fi === -1 || ti === -1) return;
    const next = [...order];
    next.splice(fi, 1); next.splice(ti, 0, fromId);
    reorder.mutate(next.map((id, i) => ({ id, order: i })));
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="sec-title" style={{ margin: 0 }}>Certificate / Training Types</div>
        <span style={{ fontSize: 9, color: 'var(--muted)' }}>Categories come from Role Requirements on the Training page</span>
      </div>
      <div id="cert-types-list">
        {items.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 11 }}>None yet.</div>
        ) : items.map((row) => (
          <div
            key={row.id}
            draggable={editingId !== row.id}
            onDragStart={(e) => { setDragId(row.id); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverId !== row.id) setDragOverId(row.id); }}
            onDragLeave={() => { if (dragOverId === row.id) setDragOverId(null); }}
            onDrop={(e) => { e.preventDefault(); if (dragId) commitReorder(dragId, row.id); setDragId(null); setDragOverId(null); }}
            onDragEnd={() => { setDragId(null); setDragOverId(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 8px', marginBottom: 3, borderRadius: 5,
              background: dragOverId === row.id ? 'rgba(232,164,39,.18)' : 'var(--surface)',
              border: dragOverId === row.id ? '1px dashed var(--accent)' : '1px solid transparent',
              opacity: dragId === row.id ? 0.4 : 1,
              cursor: editingId === row.id ? 'text' : 'grab',
            }}
          >
            <span style={{ color: 'var(--faint)', fontSize: 14, userSelect: 'none', lineHeight: 1 }} title="Drag to reorder">⠿</span>
            <span style={{ width: 18, textAlign: 'center', fontSize: 13, color: 'var(--live)', flexShrink: 0 }}>◫</span>
            {editingId === row.id ? (
              <input
                type="text"
                autoFocus
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                onBlur={() => commitRename(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitRename(row.id); }
                  if (e.key === 'Escape') { setEditingId(null); setEditingText(''); }
                }}
                style={{ flex: 1, background: 'var(--card)', border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: 'var(--text)' }}
              />
            ) : (
              <span style={{ fontSize: 11, flex: 1, cursor: 'text', minWidth: 0 }} onDoubleClick={() => { setEditingId(row.id); setEditingText(row.value); }} title="Double-click to rename">
                {row.value}
                {row.shortKey ? <span style={{ marginLeft: 6, fontSize: 9, color: 'var(--faint)', fontFamily: 'DM Mono, monospace' }}>{row.shortKey}</span> : null}
                {certCounts[row.value] && (certCounts[row.value]!.held > 0 || certCounts[row.value]!.expired > 0) ? (
                  <span style={{ marginLeft: 6 }}>
                    <UsageBadgeView
                      badge={{
                        primary: certCounts[row.value]!.held,
                        primaryLabel: 'held',
                        alert: certCounts[row.value]!.expired,
                      }}
                    />
                  </span>
                ) : null}
              </span>
            )}
            {editingId !== row.id ? (
              <button className="btn btn-ghost btn-xs" style={{ color: 'var(--muted)', padding: '1px 6px' }} onClick={() => { setEditingId(row.id); setEditingText(row.value); }} title="Rename">✏</button>
            ) : null}
            <button className="btn btn-ghost btn-xs" style={{ color: 'var(--danger)', padding: '1px 6px' }} onClick={() => { if (confirm(`Remove "${row.value}"?`)) remove.mutate(row.id); }} title="Remove">✕</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-input"
          placeholder="New certificate type…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) add.mutate(); }}
          style={{ flex: 1, minWidth: 140, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 8px', fontSize: 11, color: 'var(--text)' }}
        />
        <button className="btn btn-primary btn-sm" onClick={() => add.mutate()} disabled={add.isPending || !value.trim()}>+ Add</button>
      </div>
      <div style={{ fontSize: 9, color: 'var(--faint)', marginTop: 5 }}>💡 Drag rows to reorder · double-click name to rename</div>
    </div>
  );
}

interface ModulesCfg { modules?: Record<string, boolean>; order?: string[]; }

interface CountQueryResult { length: number }

function PillToggle({ on, disabled, onClick }: { on: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      aria-pressed={on}
      style={{
        width: 34,
        height: 19,
        borderRadius: 10,
        background: on ? 'var(--live)' : 'var(--border)',
        position: 'relative',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background .2s',
        opacity: disabled ? 0.6 : 1,
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 18 : 3,
          width: 13,
          height: 13,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,.3)',
          transition: 'left .2s',
        }}
      />
    </button>
  );
}

function ModulesCard() {
  const qc = useQueryClient();
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  const { data: cfg } = useQuery<ModulesCfg>({
    queryKey: ['settings', 'modules'],
    queryFn: () => api.get<ModulesCfg>('/settings/modules-config').then((r) => r.data).catch(() => ({})),
  });

  // Counts per module — match the prototype's badges (e.g. "13" workers, "41 certs", "5 searches").
  // Keys are shared with other pages so react-query dedupes the fetch. Do NOT add `.catch(() => [])` here:
  // it would resolve the promise with [], poisoning the shared cache for staleTime and breaking dropdowns
  // in other components (e.g. AddProjectModal Customer/Supervisor selects). On error the destructure
  // default `= []` still shows "0" here, and react-query's error state lets other consumers refetch.
  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<unknown[]>('/workers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<unknown[]>('/projects').then((r) => r.data) });
  const { data: customers = [] } = useQuery({ queryKey: ['customers'], queryFn: () => api.get<unknown[]>('/customers').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<unknown[]>('/contacts').then((r) => r.data) });
  const { data: suppliers = [] } = useQuery({ queryKey: ['suppliers'], queryFn: () => api.get<unknown[]>('/suppliers').then((r) => r.data) });
  const { data: vacancies = [] } = useQuery({ queryKey: ['vacancies'], queryFn: () => api.get<unknown[]>('/vacancies').then((r) => r.data) });
  const { data: deployments = [] } = useQuery({ queryKey: ['deployments'], queryFn: () => api.get<unknown[]>('/deployments').then((r) => r.data) });

  const certCount = useMemo(() => {
    let n = 0;
    for (const w of workers as Array<{ certs?: unknown[] }>) {
      n += (w.certs ?? []).length;
    }
    return n;
  }, [workers]);

  const moduleCount = (key: string): string | null => {
    switch (key) {
      case 'projects': return String((projects as CountQueryResult[]).length);
      case 'customers': return String((customers as CountQueryResult[]).length);
      case 'contacts': return String((contacts as CountQueryResult[]).length);
      case 'workers': return String((workers as CountQueryResult[]).length);
      case 'training': return certCount > 0 ? `${certCount} certs` : null;
      case 'vacancy': return (vacancies as CountQueryResult[]).length > 0 ? `${(vacancies as CountQueryResult[]).length} searches` : null;
      case 'deploylog': return String((deployments as CountQueryResult[]).length);
      case 'suppliers': return String((suppliers as CountQueryResult[]).length);
      default: return null;
    }
  };

  const save = useMutation({
    mutationFn: async (next: ModulesCfg) => api.patch('/settings/modules-config', next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'modules'] });
    },
    onError: () => toast.error('Save failed'),
  });

  const toggleModule = (key: string) => {
    if (CORE_MODULES.includes(key)) {
      toast.error('Cannot disable', 'Core modules cannot be disabled');
      return;
    }
    const enabled = { ...(cfg?.modules ?? {}) };
    enabled[key] = enabled[key] === false ? true : false;
    save.mutate({ ...(cfg ?? {}), modules: enabled });
  };

  const list = [...MODULES].sort((a, b) => {
    const order = cfg?.order ?? [];
    const ai = order.indexOf(a.key);
    const bi = order.indexOf(b.key);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const commitReorder = (fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    const order = [...(cfg?.order ?? list.map((m) => m.key))];
    const fromIx = order.indexOf(fromKey);
    const toIx = order.indexOf(toKey);
    if (fromIx === -1 || toIx === -1) return;
    const next = [...order];
    next.splice(fromIx, 1);
    next.splice(toIx, 0, fromKey);
    save.mutate({ ...(cfg ?? {}), order: next });
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="sec-title" style={{ margin: 0 }}>Modules</div>
        <span style={{ fontSize: 9, color: 'var(--muted)' }}>Drag to reorder · toggle to show/hide</span>
      </div>
      <div id="mod-list">
        {list.map((m) => {
          const enabled = cfg?.modules?.[m.key] !== false;
          const isCore = CORE_MODULES.includes(m.key);
          const count = moduleCount(m.key);
          return (
            <div
              key={m.key}
              draggable
              onDragStart={(e) => { setDragKey(m.key); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverKey !== m.key) setDragOverKey(m.key); }}
              onDragLeave={() => { if (dragOverKey === m.key) setDragOverKey(null); }}
              onDrop={(e) => { e.preventDefault(); if (dragKey) commitReorder(dragKey, m.key); setDragKey(null); setDragOverKey(null); }}
              onDragEnd={() => { setDragKey(null); setDragOverKey(null); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                marginBottom: 4,
                background: dragOverKey === m.key ? 'rgba(232,164,39,.18)' : 'var(--surface)',
                border: dragOverKey === m.key ? '1px dashed var(--accent)' : '1px solid transparent',
                borderRadius: 6,
                opacity: dragKey === m.key ? 0.4 : 1,
                cursor: 'grab',
              }}
            >
              <span style={{ color: 'var(--faint)', fontSize: 14, userSelect: 'none', lineHeight: 1 }} title="Drag to reorder">⠿</span>
              <span style={{ width: 18, textAlign: 'center', fontSize: 14 }}>{m.icon}</span>
              <span style={{ flex: 1, fontSize: 12, opacity: enabled ? 1 : .4, textDecoration: enabled ? 'none' : 'line-through' }}>{m.label}</span>
              {count ? (
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>{count}</span>
              ) : null}
              <PillToggle on={enabled} disabled={isCore} onClick={() => toggleModule(m.key)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompanyCard() {
  const qc = useQueryClient();
  const [editCompany, setEditCompany] = useState(false);
  const { data: company } = useQuery<CompanySettings>({
    queryKey: ['settings', 'company'],
    queryFn: () => api.get<CompanySettings>('/settings/company').then((r) => r.data),
  });
  const [form, setForm] = useState<CompanySettings>({
    name: '', shortName: '', sector: '', hq: '', address: '', phone: '', email: '', website: '',
    adminName: '', adminPhone: '', adminEmail: '',
  });

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name ?? '',
        shortName: company.shortName ?? '',
        sector: company.sector ?? '',
        hq: company.hq ?? '',
        address: company.address ?? '',
        phone: company.phone ?? '',
        email: company.email ?? '',
        website: company.website ?? '',
        adminName: company.adminName ?? '',
        adminPhone: company.adminPhone ?? '',
        adminEmail: company.adminEmail ?? '',
      });
    }
  }, [company]);

  const save = useMutation({
    mutationFn: async () => api.patch('/settings/company', {
      name: form.name,
      shortName: form.shortName || null,
      sector: form.sector || null,
      hq: form.hq || null,
      address: form.address || null,
      phone: form.phone || null,
      email: form.email || null,
      website: form.website || null,
      adminName: form.adminName || null,
      adminPhone: form.adminPhone || null,
      adminEmail: form.adminEmail || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'company'] });
      setEditCompany(false);
      toast.success('Saved', 'Company details updated');
    },
    onError: () => toast.error('Save failed'),
  });

  const setField = (k: keyof CompanySettings) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="sec-title" style={{ margin: 0 }}>Company Details</div>
        <button className="btn btn-ghost btn-sm" id="company-edit-btn" onClick={() => setEditCompany((v) => !v)}>
          {editCompany ? '✕ Close' : '✏ Edit'}
        </button>
      </div>
      <div id="company-view" style={{ display: editCompany ? 'none' : 'block' }}>
        {company ? (
          <>
            <div className="dp-row"><span className="dp-key">Company</span><span className="dp-val">{company.name || <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Short Name</span><span className="dp-val">{company.shortName ? <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--accent)' }}>{company.shortName}</span> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Sector</span><span className="dp-val">{company.sector || <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">HQ</span><span className="dp-val">{company.hq || <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Address</span><span className="dp-val" style={{ fontSize: 11 }}>{company.address || <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Phone</span><span className="dp-val">{company.phone ? <a href={`tel:${company.phone}`} style={{ color: 'var(--text)', textDecoration: 'none' }}>{company.phone}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Email</span><span className="dp-val">{company.email ? <a href={`mailto:${company.email}`} style={{ color: 'var(--info)', textDecoration: 'none' }}>{company.email}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Website</span><span className="dp-val">{company.website ? <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{company.website}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="divider"></div>
            <div className="sec-title">Admin Contact</div>
            <div className="dp-row"><span className="dp-key">Name</span><span className="dp-val" style={{ fontWeight: 500 }}>{company.adminName || <span style={{ color: 'var(--faint)', fontWeight: 400 }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Phone</span><span className="dp-val">{company.adminPhone ? <a href={`tel:${company.adminPhone}`} style={{ color: 'var(--text)', textDecoration: 'none' }}>{company.adminPhone}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
            <div className="dp-row"><span className="dp-key">Email</span><span className="dp-val">{company.adminEmail ? <a href={`mailto:${company.adminEmail}`} style={{ color: 'var(--info)', textDecoration: 'none' }}>{company.adminEmail}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
          </>
        ) : (
          <div style={{ color: 'var(--muted)', fontSize: 12 }}>Loading…</div>
        )}
      </div>
      <div id="company-edit-form" style={{ display: editCompany ? 'block' : 'none' }}>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Company Name</label><input type="text" className="form-input" id="co-name" value={form.name ?? ''} onChange={setField('name')} /></div>
          <div className="form-row"><label className="form-label">Short Name</label><input type="text" className="form-input" id="co-shortname" value={form.shortName ?? ''} onChange={setField('shortName')} /></div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Sector</label><input type="text" className="form-input" id="co-sector" value={form.sector ?? ''} onChange={setField('sector')} /></div>
          <div className="form-row"><label className="form-label">HQ Location</label><input type="text" className="form-input" id="co-hq" value={form.hq ?? ''} onChange={setField('hq')} /></div>
        </div>
        <div className="form-row"><label className="form-label">Full Address</label><input type="text" className="form-input" id="co-address" value={form.address ?? ''} onChange={setField('address')} /></div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Main Phone</label><input type="text" className="form-input" id="co-phone" value={form.phone ?? ''} onChange={setField('phone')} /></div>
          <div className="form-row"><label className="form-label">Main Email</label><input type="email" className="form-input" id="co-email" value={form.email ?? ''} onChange={setField('email')} /></div>
        </div>
        <div className="form-row"><label className="form-label">Website</label><input type="text" className="form-input" id="co-website" value={form.website ?? ''} onChange={setField('website')} /></div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 11, marginTop: 5 }}>
          <div className="sec-title" style={{ marginBottom: 9 }}>Admin Contact</div>
          <div className="fg2">
            <div className="form-row"><label className="form-label">Admin Name</label><input type="text" className="form-input" id="co-adminname" value={form.adminName ?? ''} onChange={setField('adminName')} /></div>
            <div className="form-row"><label className="form-label">Admin Phone</label><input type="text" className="form-input" id="co-adminphone" value={form.adminPhone ?? ''} onChange={setField('adminPhone')} /></div>
          </div>
          <div className="form-row"><label className="form-label">Admin Email</label><input type="email" className="form-input" id="co-adminemail" value={form.adminEmail ?? ''} onChange={setField('adminEmail')} /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7, marginTop: 11 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditCompany(false)}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={() => save.mutate()} disabled={save.isPending || !form.name?.trim()}>
            {save.isPending ? 'Saving…' : '✓ Save Details'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface WorkerForAgencyCount { id: string; agencyCode: string | null; }

function AgenciesCard() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCode, setEditCode] = useState('');
  const [editName, setEditName] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const { data: agencies = [] } = useQuery({
    queryKey: ['agencies'],
    queryFn: () => api.get<Agency[]>('/agencies').then((r) => r.data),
  });
  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerForAgencyCount[]>('/workers').then((r) => r.data).catch(() => [] as WorkerForAgencyCount[]),
  });

  const workerCountByCode = useMemo(() => {
    const m: Record<string, number> = {};
    for (const w of workers) {
      if (!w.agencyCode) continue;
      m[w.agencyCode] = (m[w.agencyCode] ?? 0) + 1;
    }
    return m;
  }, [workers]);

  const add = useMutation({
    mutationFn: async () => api.post('/agencies', {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      order: agencies.length,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agencies'] });
      setCode(''); setName('');
      toast.success('Agency added');
    },
    onError: () => toast.error('Add failed'),
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Agency> }) =>
      api.patch(`/agencies/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agencies'] }),
    onError: () => toast.error('Save failed'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`/agencies/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agencies'] });
      toast.info('Agency removed');
    },
    onError: () => toast.error('Delete failed'),
  });

  const reorder = useMutation({
    mutationFn: async (pairs: Array<{ id: string; order: number }>) =>
      Promise.all(pairs.map((p) => api.patch(`/agencies/${p.id}`, { order: p.order }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agencies'] }),
    onError: () => toast.error('Reorder failed'),
  });

  const startEdit = (a: Agency) => {
    setEditingId(a.id);
    setEditCode(a.code);
    setEditName(a.name);
  };

  const commitEdit = () => {
    if (!editingId) return;
    const cur = agencies.find((a) => a.id === editingId);
    const patch: Partial<Agency> = {};
    if (editCode.trim() && editCode.trim().toUpperCase() !== cur?.code) patch.code = editCode.trim().toUpperCase();
    if (editName.trim() && editName.trim() !== cur?.name) patch.name = editName.trim();
    if (Object.keys(patch).length > 0) update.mutate({ id: editingId, patch });
    setEditingId(null);
    setEditCode('');
    setEditName('');
  };

  const commitReorder = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const order = agencies.map((a) => a.id);
    const fi = order.indexOf(fromId);
    const ti = order.indexOf(toId);
    if (fi === -1 || ti === -1) return;
    const next = [...order];
    next.splice(fi, 1);
    next.splice(ti, 0, fromId);
    reorder.mutate(next.map((id, i) => ({ id, order: i })));
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="sec-title" style={{ margin: 0 }}>Agencies</div>
        <span style={{ fontSize: 9, color: 'var(--muted)' }}>Manage agency list · used in training &amp; timesheets</span>
      </div>
      <div id="settings-agencies-list">
        {agencies.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 11 }}>None yet.</div>
        ) : agencies.map((a) => {
          const isEditing = editingId === a.id;
          const workerCount = workerCountByCode[a.code] ?? 0;
          return (
            <div
              key={a.id}
              draggable={!isEditing}
              onDragStart={(e) => { setDragId(a.id); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverId !== a.id) setDragOverId(a.id); }}
              onDragLeave={() => { if (dragOverId === a.id) setDragOverId(null); }}
              onDrop={(e) => { e.preventDefault(); if (dragId) commitReorder(dragId, a.id); setDragId(null); setDragOverId(null); }}
              onDragEnd={() => { setDragId(null); setDragOverId(null); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 8px',
                marginBottom: 3,
                borderRadius: 5,
                background: dragOverId === a.id ? 'rgba(232,164,39,.18)' : 'var(--surface)',
                border: dragOverId === a.id ? '1px dashed var(--accent)' : '1px solid transparent',
                opacity: dragId === a.id ? 0.4 : (a.active ? 1 : 0.6),
                cursor: isEditing ? 'text' : 'grab',
              }}
            >
              <span style={{ color: 'var(--faint)', fontSize: 14, userSelect: 'none', lineHeight: 1 }} title="Drag to reorder">⠿</span>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    autoFocus
                    value={editCode}
                    maxLength={10}
                    onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitEdit(); } if (e.key === 'Escape') { setEditingId(null); } }}
                    style={{ width: 80, background: 'var(--card)', border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}
                  />
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitEdit(); } if (e.key === 'Escape') { setEditingId(null); } }}
                    style={{ flex: 1, background: 'var(--card)', border: '1px solid var(--accent)', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: 'var(--text)' }}
                  />
                  <button className="btn btn-primary btn-xs" style={{ padding: '1px 8px' }} onClick={commitEdit}>Save</button>
                  <button className="btn btn-ghost btn-xs" style={{ padding: '1px 8px' }} onClick={() => setEditingId(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <span className={`mx-ag mx-ag-${a.code}`} style={{ flexShrink: 0 }}>{a.code}</span>
                  <span style={{ flex: 1, fontSize: 11 }}>{a.name}</span>
                  {workerCount > 0 ? (
                    <span style={{ fontSize: 9, color: 'var(--muted)', padding: '1px 6px', borderRadius: 4, background: 'var(--card)' }}>
                      {workerCount} worker{workerCount !== 1 ? 's' : ''}
                    </span>
                  ) : null}
                  <span
                    onClick={() => update.mutate({ id: a.id, patch: { active: !a.active } })}
                    title={a.active ? 'Click to deactivate' : 'Click to activate'}
                    style={{
                      fontSize: 9,
                      padding: '2px 8px',
                      background: a.active ? 'rgba(34,197,94,.15)' : 'rgba(107,122,153,.15)',
                      color: a.active ? 'var(--live)' : 'var(--muted)',
                      borderRadius: 4,
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      letterSpacing: '.3px',
                    }}
                  >
                    {a.active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    className="btn btn-ghost btn-xs"
                    style={{ color: 'var(--muted)', padding: '1px 6px' }}
                    onClick={() => startEdit(a)}
                    title="Rename"
                  >✏</button>
                  <button
                    className="btn btn-ghost btn-xs"
                    style={{ color: 'var(--danger)', padding: '1px 6px' }}
                    onClick={() => { if (confirm(`Remove agency "${a.code} — ${a.name}"?`)) remove.mutate(a.id); }}
                    title="Remove"
                  >✕</button>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input
          type="text"
          className="form-input"
          id="settings-new-agency-code"
          placeholder="Code (e.g. XYZ)"
          maxLength={10}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          style={{ width: 100, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 8px', fontSize: 11, color: 'var(--text)' }}
        />
        <input
          type="text"
          className="form-input"
          id="settings-new-agency-name"
          placeholder="Full agency name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 8px', fontSize: 11, color: 'var(--text)' }}
        />
        <button className="btn btn-primary btn-sm" onClick={() => add.mutate()} disabled={add.isPending || !code.trim() || !name.trim()}>+ Add</button>
      </div>
      <div style={{ fontSize: 9, color: 'var(--faint)', marginTop: 5 }}>💡 Drag rows to reorder · click status pill to toggle active · ✏ to rename code or name</div>
    </div>
  );
}

interface WorkerForRoleCount { id: string; role: string; status: string; }
interface SupplierForCategoryCount { id: string; category: string; }

const CORE_STATUSES = ['available', 'deployed', 'unavailable'] as const;

export function Settings() {
  const { canManageSettings } = useRolePerms();
  const [roleManagerInitial, setRoleManagerInitial] = useState<string | null>(null);

  const { data: workersForCounts = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerForRoleCount[]>('/workers').then((r) => r.data).catch(() => [] as WorkerForRoleCount[]),
    enabled: canManageSettings,
  });
  const { data: suppliersForCounts = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<SupplierForCategoryCount[]>('/suppliers').then((r) => r.data).catch(() => [] as SupplierForCategoryCount[]),
    enabled: canManageSettings,
  });

  const roleCounts = useMemo(() => {
    const m: Record<string, UsageBadge> = {};
    for (const w of workersForCounts) {
      if (!w.role) continue;
      m[w.role] = { ...(m[w.role] ?? {}), primary: (m[w.role]?.primary ?? 0) + 1, primaryLabel: m[w.role]?.primaryLabel ?? 'workers' };
    }
    // Singular label when count is 1
    for (const k of Object.keys(m)) {
      if (m[k]!.primary === 1) m[k]!.primaryLabel = 'worker';
    }
    return m;
  }, [workersForCounts]);

  const statusCounts = useMemo(() => {
    const m: Record<string, UsageBadge> = {};
    for (const w of workersForCounts) {
      const s = w.status;
      if (!s) continue;
      m[s] = { ...(m[s] ?? {}), primary: (m[s]?.primary ?? 0) + 1, primaryLabel: m[s]?.primaryLabel ?? 'workers' };
    }
    for (const k of Object.keys(m)) {
      if (m[k]!.primary === 1) m[k]!.primaryLabel = 'worker';
    }
    return m;
  }, [workersForCounts]);

  const supplierCategoryCounts = useMemo(() => {
    const m: Record<string, UsageBadge> = {};
    for (const s of suppliersForCounts) {
      if (!s.category) continue;
      m[s.category] = { ...(m[s.category] ?? {}), primary: (m[s.category]?.primary ?? 0) + 1, primaryLabel: m[s.category]?.primaryLabel ?? 'suppliers' };
    }
    for (const k of Object.keys(m)) {
      if (m[k]!.primary === 1) m[k]!.primaryLabel = 'supplier';
    }
    return m;
  }, [suppliersForCounts]);

  if (!canManageSettings) {
    return (
      <div className="page" id="page-settings">
        <div className="hbar">
          <div>
            <div className="ph">Settings</div>
            <div className="ps">Manage dropdown lists and system configuration</div>
          </div>
        </div>
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
          🔒 Access Restricted — Director role required
        </div>
      </div>
    );
  }

  const roleExtraActions = (row: SettingRow) => (
    <button
      className="btn btn-ghost btn-xs"
      style={{ color: 'var(--accent)', padding: '1px 6px', marginRight: 2 }}
      onClick={() => setRoleManagerInitial(row.value)}
      title="Configure mandatory & beneficial certs for this role"
    >⚙</button>
  );

  const statusBadgeRenderer = (row: SettingRow) => {
    const v = row.value.toLowerCase();
    let badgeClass = 'b-tag';
    if (v === 'available') badgeClass = 'b-valid';
    else if (v === 'deployed') badgeClass = 'b-tag';
    else if (v === 'unavailable') badgeClass = 'b-expired';
    else if (v.includes('annual') || v.includes('leave') || v.includes('sick')) badgeClass = 'b-pending';
    return <span className={`badge ${badgeClass}`} style={{ fontSize: 10 }}>{row.value}</span>;
  };

  return (
    <div className="page" id="page-settings">
      <div className="hbar">
        <div>
          <div className="ph">Settings</div>
          <div className="ps">Manage dropdown lists and system configuration</div>
        </div>
      </div>
      <div className="g2">
        <ReferenceHealthCard />
        <CompanyCard />
        <EditableCard
          title="Worker Roles"
          table="roles"
          queryKey={['settings', 'roles']}
          placeholder="New role name…"
          sub="Used in worker profile & vacancy matching · ⚙ to configure required certs"
          listId="settings-roles-list"
          icon="◈"
          iconColor="#a78bfa"
          usageCounts={roleCounts}
          extraActions={roleExtraActions}
        />
        <EditableCard
          title="Worker Status Options"
          table="statuses"
          queryKey={['settings', 'statuses']}
          placeholder="New status option…"
          listId="settings-status-list"
          valueRenderer={statusBadgeRenderer}
          usageCounts={statusCounts}
          coreValues={CORE_STATUSES}
        />
        <CertTypesCard />
        <EditableCard
          title="Supplier Categories"
          table="supplier-categories"
          queryKey={['settings', 'supplier-categories']}
          placeholder="New supplier category…"
          listId="sup-cats"
          icon="◈"
          iconColor="var(--accent)"
          usageCounts={supplierCategoryCounts}
        />
        <EditableCard
          title="Project Document Types"
          table="doc-types"
          queryKey={['settings', 'doc-types']}
          placeholder="New document type…"
          listId="settings-doctype-list"
          icon="📄"
        />
        <EditableCard
          title="Deployment Duration Options"
          table="durations"
          queryKey={['settings', 'durations']}
          placeholder="e.g. 6 weeks…"
          listId="settings-duration-list"
          icon="⏱"
          iconColor="var(--muted)"
        />
        <AgenciesCard />
        <ModulesCard />
      </div>
      {roleManagerInitial !== null ? (
        <RoleManagerModal
          onClose={() => setRoleManagerInitial(null)}
          initialEditRole={roleManagerInitial || undefined}
        />
      ) : null}
    </div>
  );
}
